/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  PreviewDirectoryRoleSetupRequest,
  PreviewDirectoryRoleSetupSecurity,
} from "../models/operations/previewdirectoryrolesetup.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDirectoryRoleSetupPreviewQuery,
  DirectoryRoleSetupPreviewQueryData,
  prefetchDirectoryRoleSetupPreview,
  queryKeyDirectoryRoleSetupPreview,
} from "./directoryRoleSetupPreview.core.js";
export {
  buildDirectoryRoleSetupPreviewQuery,
  type DirectoryRoleSetupPreviewQueryData,
  prefetchDirectoryRoleSetupPreview,
  queryKeyDirectoryRoleSetupPreview,
};

export type DirectoryRoleSetupPreviewQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * previewDirectoryRoleSetup access
 *
 * @remarks
 * Propose roles for every live directory group and mappable attribute value, for bulk setup. An unmapped source is proposed one role: the live role whose slug matches the source name, or a new role named after the source. A mapped source is listed with its current roles. Nothing is written. Each item carries a version that applyDirectoryRoleSetup checks to detect changes since the preview; the version authorizes nothing.
 */
export function useDirectoryRoleSetupPreview(
  request?: PreviewDirectoryRoleSetupRequest | undefined,
  security?: PreviewDirectoryRoleSetupSecurity | undefined,
  options?: QueryHookOptions<
    DirectoryRoleSetupPreviewQueryData,
    DirectoryRoleSetupPreviewQueryError
  >,
): UseQueryResult<
  DirectoryRoleSetupPreviewQueryData,
  DirectoryRoleSetupPreviewQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDirectoryRoleSetupPreviewQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * previewDirectoryRoleSetup access
 *
 * @remarks
 * Propose roles for every live directory group and mappable attribute value, for bulk setup. An unmapped source is proposed one role: the live role whose slug matches the source name, or a new role named after the source. A mapped source is listed with its current roles. Nothing is written. Each item carries a version that applyDirectoryRoleSetup checks to detect changes since the preview; the version authorizes nothing.
 */
export function useDirectoryRoleSetupPreviewSuspense(
  request?: PreviewDirectoryRoleSetupRequest | undefined,
  security?: PreviewDirectoryRoleSetupSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DirectoryRoleSetupPreviewQueryData,
    DirectoryRoleSetupPreviewQueryError
  >,
): UseSuspenseQueryResult<
  DirectoryRoleSetupPreviewQueryData,
  DirectoryRoleSetupPreviewQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDirectoryRoleSetupPreviewQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDirectoryRoleSetupPreviewData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DirectoryRoleSetupPreviewQueryData,
): DirectoryRoleSetupPreviewQueryData | undefined {
  const key = queryKeyDirectoryRoleSetupPreview(...queryKeyBase);

  return client.setQueryData<DirectoryRoleSetupPreviewQueryData>(key, data);
}

export function invalidateDirectoryRoleSetupPreview(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "previewDirectoryRoleSetup",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDirectoryRoleSetupPreview(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "previewDirectoryRoleSetup"],
  });
}
